import { useQuery } from '@tanstack/react-query'
import { dayLabel, localClockOf, requestDate, requestWindowLabel } from '@kronos/domain'
import { repository } from '../../data'
import type { TeamTodayView } from '../../data/schedule'
import { Link } from '../shell/Link'
import { Panel, Section } from '../primitives'
import { backedOutLine } from './PendingList'
import { TODAY_KEY } from './shared'

/**
 * The engineer's block on Today, one line per question: who is in right now,
 * who is coming today, who has not answered, and — gently, last — who has not
 * said anything about next week. Computed by `teamToday` on the server.
 */
export function TodayTeam(): JSX.Element | null {
  const today = useQuery({ queryKey: TODAY_KEY, queryFn: () => repository.teamToday() })
  const data = today.data
  if (!data) return null
  const { onShift, scheduledToday, awaiting, backedOut, unsetNextWeek } = data
  if (onShift.length + scheduledToday.length + awaiting.length + backedOut.length + unsetNextWeek.length === 0) return null

  return (
    <Section
      label="Team today"
      actions={
        <Link to={{ name: 'people', section: 'schedule' }} className="button section-button">
          Schedule
        </Link>
      }
    >
      <Panel>
        <div className="row-list team-today">
          <Line label="On shift now">
            {onShift.length === 0
              ? 'Nobody'
              : onShift.map((s) => `${s.technician.name} since ${localClockOf(s.since, data.timeZone)}`).join(', ')}
          </Line>
          <Line label="Coming in today">
            {scheduledToday.length === 0
              ? 'Nobody booked'
              : scheduledToday.map((r) => `${r.technicianName} ${requestWindowLabel(r, data.timeZone)}`).join(', ')}
          </Line>
          {backedOut.length > 0 ? (
            <Line label="Can’t make it" list warn>
              {backedOut.map((r) => (
                <span key={r.id}>
                  {backedOutLine(r, data.timeZone)}
                  {r.reply ? <span className="muted"> · “{r.reply}”</span> : null}
                </span>
              ))}
            </Line>
          ) : null}
          {awaiting.length > 0 ? <Awaiting data={data} /> : null}
          {unsetNextWeek.length > 0 ? (
            <Line label="No availability next week" quiet>
              {unsetNextWeek.map((t) => t.name).join(', ')}
            </Line>
          ) : null}
        </div>
      </Panel>
    </Section>
  )
}

function Awaiting({ data }: { data: TeamTodayView }): JSX.Element {
  const shown = data.awaiting.slice(0, 3)
  const more = data.awaiting.length - shown.length
  return (
    <Line label={`Awaiting an answer · ${data.awaiting.length}`} list>
      {shown.map((r) => (
        <span key={r.id}>
          {r.technicianName} · {dayLabel(requestDate(r, data.timeZone))} {requestWindowLabel(r, data.timeZone)}
        </span>
      ))}
      {more > 0 ? <span className="muted">and {more} more</span> : null}
    </Line>
  )
}

function Line({
  label,
  quiet = false,
  list = false,
  warn = false,
  children,
}: {
  label: string
  quiet?: boolean
  list?: boolean
  warn?: boolean
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className={`team-today__line${quiet ? ' team-today__line--quiet' : ''}${warn ? ' team-today__line--warn' : ''}`}>
      <span className="team-today__label">{label}</span>
      <span className={`team-today__value${list ? ' team-today__value--list' : ''}`}>{children}</span>
    </div>
  )
}
